import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import moment from 'moment-timezone';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { CardSkeleton, DataTable, EmptyState, LoadingButton, Notice, PageHeader, StatCard, type DataTableColumn } from '@routes-plan/ui';
import { minYmd, nowOperationalDateString, operationalMoment } from '../utils/operationalDateTime';
import { useSelection } from '../components/context/SelectionContext';
import { formatCurrency, companyLocale } from '../utils/format/currency';
import { Office } from '../models/office';
import { Route } from '../models/route';
import OnboardingCard from '../components/onboarding/OnboardingCard';
import { RuttiaIcon } from '../components/icons/RuttiaIcon';
import type { GlyphName } from '../components/icons/glyphs';
import { isSuperAdminUser } from '../utils/authScope';
import { useResourcePermissions } from '../hooks/useResourcePermissions';
import { setPageTitle } from '../utils/pageTitle';
import { ScreenTourButton } from '../components/help/ScreenHelp';
import { useT } from '../i18n';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface SummaryTotals {
  totalPayments: number;
  totalLoans: number;
  totalExpenses: number;
  totalDiscounts: number;
  totalInterest: number;
  totalRefinance: number;
  valueRefinance: number;
  base: number;
  cash: number;
  expectedCash: number;
  cashDifference: number;
  countPayments: number;
  countLoans: number;
  countClients: number;
  countCancel: number;
  totalCancel: number;
  totalOffExpPlus: number;
  totalOffExpMinus: number;
  defaultClientCount: number;
  defaultClientValue: number;
  routePercentage: number;
}

interface ChartPoint {
  date: string;
  totalPayments: number;
  totalLoans: number;
  totalExpenses: number;
  cashDifference: number;
}

interface RouteSummaryRow {
  id: number;
  route: string;
  totalPayments: number;
  totalLoans: number;
  totalExpenses: number;
  totalDiscounts: number;
  totalInterest: number;
  cashDifference: number;
  countClients: number;
}

interface SuperadminOverviewPayload {
  officeCount: number;
  routeCount: number;
  totalClients: number;
  openLoansTotal: number;
  closedLoansTotal: number;
  visitOrderLoansTodayTotal?: number;
  operationalDate?: string;
  rows: Array<{
    officeId: number;
    officeName: string;
    routeId: number;
    routeName: string;
    clientCount: number;
    openLoans: number;
    closedLoans: number;
    visitOrderLoansToday?: number;
  }>;
}

interface OfficeAggregateRow {
  id: string;
  officeName: string;
  routeCount: number;
  clientCount: number;
  openLoans: number;
  closedLoans: number;
  visitOrderLoansToday: number;
}

interface RouteDetailRow {
  id: number;
  officeName: string;
  routeName: string;
  clientCount: number;
  openLoans: number;
  closedLoans: number;
  visitOrderLoansToday: number;
}

interface Metric {
  key: string;
  label: string;
  value: string;
  hint?: string;
  icon: GlyphName;
}

/** Rejilla de indicadores: tantas columnas como quepan (una en teléfono). */
const statGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
} as const;

const count = (value: unknown) => Number(value ?? 0).toLocaleString(companyLocale());

/**
 * Inicio: primeros pasos de la empresa (mientras falte algo), el resumen diario de una ruta (indicadores,
 * gráfico por día y tabla por ruta) y la vista global de las sedes y rutas que la persona puede ver.
 */
const Dashboard: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('dashboard.title');
  const navigate = useNavigate();
  const {
    office_id,
    setOffice_id,
    collect_id,
    setCollect_id,
    setUser_id,
  } = useSelection();
  const theme = useTheme();

  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Route[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>(collect_id ? [collect_id] : []);
  const [startDate, setStartDate] = useState<string>(operationalMoment().subtract(0, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(nowOperationalDateString());
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summaryTotals, setSummaryTotals] = useState<SummaryTotals | null>(null);
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [routeSummaryRows, setRouteSummaryRows] = useState<RouteSummaryRow[]>([]);
  const [totalEntries, setTotalEntries] = useState<number>(0);
  const [autoFetched, setAutoFetched] = useState<boolean>(false);
  const [authUser, setAuthUser] = useState<{ isSystem?: number; username?: string } | null>(null);
  const [mainTab, setMainTab] = useState(0);
  const [superOverview, setSuperOverview] = useState<SuperadminOverviewPayload | null>(null);
  const [superOverviewLoading, setSuperOverviewLoading] = useState(false);
  const [superOverviewLoaded, setSuperOverviewLoaded] = useState(false);
  const { canRead: canReadDashboardSummary } = useResourcePermissions('/dashboard');
  const { canRead: canReadResumenDia } = useResourcePermissions('/reports/daily-summary');
  const { canRead: canReadExpenseReport } = useResourcePermissions('/reports/expenses');

  const isSuperAdmin = isSuperAdminUser(authUser);
  const canReadSummaryDay = canReadDashboardSummary || canReadResumenDia || canReadExpenseReport;

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  useEffect(() => {
    const fetchInitialData = async () => {
      showLoading();
      try {
        const { data: userResp } = await axios.get('user');
        const me = userResp?.data;
        setAuthUser(me ?? null);
        setUser_id(me?.id);

        const officesUrl = isSuperAdminUser(me) ? 'offices/all' : 'offices';
        const { data: officesBody } = await axios.get(officesUrl);
        const officeList: Office[] = Array.isArray(officesBody?.data) ? officesBody.data : [];
        setOffices(officeList);

        if (!office_id && officeList.length > 0) {
          setOffice_id(String(officeList[0].id));
        }
      } catch (error) {
        console.error(error);
        showAlert(t('dashboard.initialError'), 'error');
      } finally {
        hideLoading();
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchRoutes = async () => {
      if (!office_id) {
        setCollectsOffice([]);
        setCollect_id('');
        return;
      }

      try {
        /** El API filtra por permisos; el superadmin recibe todas las rutas de la sede. */
        const { data } = await axios.get(`offices/${office_id}/routes`);
        const routeList: Route[] = Array.isArray(data) ? data : [];
        setCollectsOffice(routeList);

        if (!collect_id && routeList.length > 0) {
          setCollect_id(String(routeList[0].id));
        }
      } catch (error) {
        console.error(error);
        showAlert(t('dashboard.routesError'), 'error');
        setCollectsOffice([]);
      }
    };

    fetchRoutes();
  }, [office_id]);

  useEffect(() => {
    if (collect_id) {
      setSelectedCollects([collect_id]);
    } else {
      setSelectedCollects([]);
    }
  }, [collect_id]);

  useEffect(() => {
    setSummaryTotals(null);
    setRouteSummaryRows([]);
    setChartData([]);
    setTotalEntries(0);
  }, [office_id, collect_id]);

  const aggregateByRoute = useCallback((data: any[]): RouteSummaryRow[] => {
    return data.map((item) => {
      const totals = (item.data || []).reduce(
        (acc: any, closer: any) => {
          acc.totalPayments += Number(closer.paymentTotal) || 0;
          acc.totalLoans += Number(closer.loanTotal) || 0;
          acc.totalExpenses += Number(closer.expenseTotal) || 0;
          acc.totalDiscounts += Number(closer.discountTotal) || 0;
          acc.totalInterest += Number(closer.interestTotal) || 0;
          acc.totalOffExpPlus += Number(closer.otherIncomeTotal) || 0;
          acc.totalOffExpMinus += Number(closer.otherExpenseTotal) || 0;
          acc.cashDifference += Number(closer.cashDifference) || 0;
          acc.countClients += Number(closer.clientCount) || 0;
          return acc;
        },
        {
          totalPayments: 0,
          totalLoans: 0,
          totalExpenses: 0,
          totalDiscounts: 0,
          totalInterest: 0,
          totalOffExpPlus: 0,
          totalOffExpMinus: 0,
          cashDifference: 0,
          countClients: 0,
        }
      );

      return {
        id: item.routeId,
        route: item.routeName,
        totalPayments: totals.totalPayments,
        totalLoans: totals.totalLoans,
        totalExpenses: totals.totalExpenses,
        totalDiscounts: totals.totalDiscounts,
        totalInterest: totals.totalInterest,
        cashDifference: totals.cashDifference,
        countClients: totals.countClients,
      };
    });
  }, []);

  const handleFetchSummary = useCallback(async () => {
    if (!selectedCollects.length) {
      showAlert(t('dashboard.pickRoute'), 'warning');
      return;
    }

    const routeIds = selectedCollects
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

    if (!routeIds.length) {
      showAlert(t('dashboard.invalidRoute'), 'warning');
      return;
    }

    if (!canReadSummaryDay) {
      setSummaryTotals(null);
      setRouteSummaryRows([]);
      setChartData([]);
      setTotalEntries(0);
      showAlert(t('dashboard.noPermission'), 'warning');
      return;
    }

    try {
      setSummaryLoading(true);
      const payload = {
        startDate,
        endDate,
        routeId: routeIds,
      };

      const response = await axios.post('/reports/daily-summary', payload);
      const data = response?.data;
      const summaryByRoute = data?.data || [];
      const closers = summaryByRoute.flatMap((item: any) => item.data || []);

      if (!closers.length) {
        setSummaryTotals(null);
        setRouteSummaryRows([]);
        setChartData([]);
        setTotalEntries(0);
        showAlert(t('dashboard.noRecords'), 'info');
        setAutoFetched(true);
        return;
      }

      const aggregatedTotals = closers.reduce(
        (acc: any, closer: any) => {
          acc.totalPayments += Number(closer.paymentTotal) || 0;
          acc.totalLoans += Number(closer.loanTotal) || 0;
          acc.totalExpenses += Number(closer.expenseTotal) || 0;
          acc.totalDiscounts += Number(closer.discountTotal) || 0;
          acc.totalInterest += Number(closer.interestTotal) || 0;
          acc.totalRefinance += Number(closer.refinanceTotal) || 0;
          acc.valueRefinance += Number(closer.refinanceValue) || 0;
          acc.base += Number(closer.base) || 0;
          acc.cash += Number(closer.cash) || 0;
          acc.expectedCash += Number(closer.expectedCash) || 0;
          acc.cashDifference += Number(closer.cashDifference) || 0;
          acc.countPayments += Number(closer.paymentCount) || 0;
          acc.countLoans += Number(closer.loanCount) || 0;
          acc.countClients += Number(closer.clientCount) || 0;
          acc.countCancel += Number(closer.cancellationCount) || 0;
          acc.totalCancel += Number(closer.cancellationTotal) || 0;
          acc.totalOffExpPlus += Number(closer.otherIncomeTotal) || 0;
          acc.totalOffExpMinus += Number(closer.otherExpenseTotal) || 0;
          acc.defaultClientCount += Number(closer.defaultClientCount) || 0;
          acc.defaultClientValue += Number(closer.defaultClientValue) || 0;
          acc.routePercentage += Number(closer.routePercentage) || 0;
          acc.entries += 1;
          return acc;
        },
        {
          totalPayments: 0,
          totalLoans: 0,
          totalExpenses: 0,
          totalDiscounts: 0,
          totalInterest: 0,
          totalRefinance: 0,
          valueRefinance: 0,
          base: 0,
          cash: 0,
          expectedCash: 0,
          cashDifference: 0,
          countPayments: 0,
          countLoans: 0,
          countClients: 0,
          countCancel: 0,
          totalCancel: 0,
          totalOffExpPlus: 0,
          totalOffExpMinus: 0,
          defaultClientCount: 0,
          defaultClientValue: 0,
          routePercentage: 0,
          entries: 0,
        }
      );

      const summary: SummaryTotals = {
        totalPayments: aggregatedTotals.totalPayments,
        totalLoans: aggregatedTotals.totalLoans,
        totalExpenses: aggregatedTotals.totalExpenses,
        totalDiscounts: aggregatedTotals.totalDiscounts,
        totalInterest: aggregatedTotals.totalInterest,
        totalRefinance: aggregatedTotals.totalRefinance,
        valueRefinance: aggregatedTotals.valueRefinance,
        base: aggregatedTotals.base,
        cash: aggregatedTotals.cash,
        expectedCash: aggregatedTotals.expectedCash,
        cashDifference: aggregatedTotals.cashDifference,
        countPayments: aggregatedTotals.countPayments,
        countLoans: aggregatedTotals.countLoans,
        countClients: aggregatedTotals.countClients,
        countCancel: aggregatedTotals.countCancel,
        totalCancel: aggregatedTotals.totalCancel,
        totalOffExpPlus: aggregatedTotals.totalOffExpPlus,
        totalOffExpMinus: aggregatedTotals.totalOffExpMinus,
        defaultClientCount: aggregatedTotals.defaultClientCount,
        defaultClientValue: aggregatedTotals.defaultClientValue,
        routePercentage: aggregatedTotals.entries
          ? aggregatedTotals.routePercentage / aggregatedTotals.entries
          : 0,
      };

      const chartMap = new Map<string, ChartPoint>();
      closers.forEach((closer: any) => {
        const day = closer.date ? String(closer.date).slice(0, 10) : '';
        const current = chartMap.get(day) || {
          date: day,
          totalPayments: 0,
          totalLoans: 0,
          totalExpenses: 0,
          cashDifference: 0,
        };

        current.totalPayments += Number(closer.paymentTotal) || 0;
        current.totalLoans += Number(closer.loanTotal) || 0;
        current.totalExpenses += Number(closer.expenseTotal) || 0;
        current.cashDifference += Number(closer.cashDifference) || 0;

        chartMap.set(day, current);
      });

      const chartPoints = Array.from(chartMap.values()).sort((a, b) => a.date.localeCompare(b.date));

      setSummaryTotals(summary);
      setRouteSummaryRows(aggregateByRoute(summaryByRoute));
      setChartData(chartPoints);
      setTotalEntries(closers.length);
      setAutoFetched(true);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        showAlert(t('dashboard.noPermission'), 'error');
        return;
      }
      console.error(error);
      showAlert(t('dashboard.summaryError'), 'error');
    } finally {
      setSummaryLoading(false);
    }
  }, [aggregateByRoute, canReadSummaryDay, selectedCollects, startDate, endDate, showAlert, t]);

  const money = (value: unknown) => formatCurrency(Number(value || 0));

  const routeSummaryColumns: DataTableColumn<RouteSummaryRow>[] = useMemo(
    () => [
      { id: 'route', header: t('dashboard.col.route'), value: (r) => r.route, sortable: true },
      { id: 'totalPayments', header: t('dashboard.col.payments'), value: (r) => r.totalPayments, render: (r) => money(r.totalPayments), align: 'right', sortable: true },
      { id: 'totalLoans', header: t('dashboard.col.loans'), value: (r) => r.totalLoans, render: (r) => money(r.totalLoans), align: 'right', sortable: true },
      { id: 'totalExpenses', header: t('dashboard.col.expenses'), value: (r) => r.totalExpenses, render: (r) => money(r.totalExpenses), align: 'right', sortable: true },
      { id: 'totalDiscounts', header: t('dashboard.col.discounts'), value: (r) => r.totalDiscounts, render: (r) => money(r.totalDiscounts), align: 'right', sortable: true, hideBelow: 'md' },
      { id: 'totalInterest', header: t('dashboard.col.interest'), value: (r) => r.totalInterest, render: (r) => money(r.totalInterest), align: 'right', sortable: true },
      { id: 'cashDifference', header: t('dashboard.col.cashDifference'), value: (r) => r.cashDifference, render: (r) => money(r.cashDifference), align: 'right', sortable: true },
      { id: 'countClients', header: t('dashboard.col.clients'), value: (r) => r.countClients, render: (r) => count(r.countClients), align: 'right', sortable: true },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const metrics: Metric[] = useMemo(() => {
    if (!summaryTotals) return [];

    const netResult = summaryTotals.base
      + summaryTotals.totalPayments
      - summaryTotals.totalExpenses
      - summaryTotals.totalDiscounts
      - summaryTotals.totalLoans;
    const cashVariance = summaryTotals.cash - summaryTotals.expectedCash;

    return [
      { key: 'payments', label: t('dashboard.metric.payments'), value: formatCurrency(summaryTotals.totalPayments), icon: 'payment' },
      { key: 'loans', label: t('dashboard.metric.loans'), value: formatCurrency(summaryTotals.totalLoans), icon: 'loan' },
      { key: 'expenses', label: t('dashboard.metric.expenses'), value: formatCurrency(summaryTotals.totalExpenses), icon: 'expense' },
      { key: 'discounts', label: t('dashboard.metric.discounts'), value: formatCurrency(summaryTotals.totalDiscounts), icon: 'receipt' },
      { key: 'interest', label: t('dashboard.metric.interest'), value: formatCurrency(summaryTotals.totalInterest), icon: 'summary' },
      {
        key: 'refinance',
        label: t('dashboard.metric.refinance'),
        value: formatCurrency(summaryTotals.totalRefinance),
        hint: t('dashboard.metric.refinanceHint', { value: formatCurrency(summaryTotals.valueRefinance) }),
        icon: 'refinance',
      },
      { key: 'clients', label: t('dashboard.metric.clients'), value: count(summaryTotals.countClients), icon: 'clients' },
      { key: 'paymentCount', label: t('dashboard.metric.paymentCount'), value: count(summaryTotals.countPayments), icon: 'paid' },
      {
        key: 'cash',
        label: t('dashboard.metric.cash'),
        value: formatCurrency(summaryTotals.cash),
        hint: t('dashboard.metric.cashHint', { value: formatCurrency(summaryTotals.expectedCash) }),
        icon: 'cash-box',
      },
      {
        key: 'cashDifference',
        label: t('dashboard.metric.cashDifference'),
        value: formatCurrency(summaryTotals.cashDifference),
        hint: cashVariance === 0 ? t('dashboard.metric.noDifference') : t('dashboard.metric.difference', { value: formatCurrency(cashVariance) }),
        icon: 'end-day',
      },
      { key: 'net', label: t('dashboard.metric.net'), value: formatCurrency(netResult), icon: 'report' },
      {
        key: 'coverage',
        label: t('dashboard.metric.coverage'),
        value: `${summaryTotals.routePercentage.toFixed(1)}%`,
        hint: t('dashboard.metric.coverageHint'),
        icon: 'route',
      },
    ];
  }, [summaryTotals, t]);

  const handleOfficeSelect = (value: string) => {
    setOffice_id(value);
    setCollect_id('');
    setSelectedCollects([]);
    setSummaryTotals(null);
    setRouteSummaryRows([]);
    setChartData([]);
    setTotalEntries(0);
    setAutoFetched(false);
  };

  const handleCollectSelect = (value: string) => {
    setCollect_id(value);
    if (value) {
      setSelectedCollects([value]);
    } else {
      setSelectedCollects([]);
    }
    setSummaryTotals(null);
    setRouteSummaryRows([]);
    setChartData([]);
    setTotalEntries(0);
    setAutoFetched(false);
  };

  useEffect(() => {
    if (!autoFetched && selectedCollects.length > 0 && !summaryLoading) {
      handleFetchSummary();
    }
  }, [autoFetched, selectedCollects, summaryLoading, handleFetchSummary]);

  const loadSuperadminOverview = useCallback(async () => {
    setSuperOverviewLoading(true);
    try {
      const { data } = await axios.get<{ data: SuperadminOverviewPayload }>('dashboard/global-org-overview', {
        params: { date: nowOperationalDateString() },
      });
      setSuperOverview(data?.data ?? null);
    } catch (e: unknown) {
      let suffix = '';
      if (axios.isAxiosError(e)) {
        const st = e.response?.status;
        const body = e.response?.data as { message?: string } | undefined;
        const apiMsg = body?.message && String(body.message).trim();
        if (apiMsg) {
          suffix = ` ${apiMsg}`;
        } else if (st === 404) {
          suffix = ' (HTTP 404)';
        } else if (st === 401 || st === 403) {
          suffix = ` (HTTP ${st})`;
        } else if (st) {
          suffix = ` (HTTP ${st})`;
        } else if (e.code === 'ERR_NETWORK') {
          suffix = ' (ERR_NETWORK)';
        }
        console.error('loadSuperadminOverview', e.response?.data ?? e.message);
      }
      showAlert(`${t('dashboard.global.loadError')}${suffix}`.trim(), 'error');
      setSuperOverview(null);
    } finally {
      setSuperOverviewLoaded(true);
      setSuperOverviewLoading(false);
    }
  }, [showAlert, t]);

  useEffect(() => {
    if (mainTab === 1 && !superOverviewLoaded && !superOverviewLoading) {
      loadSuperadminOverview();
    }
  }, [mainTab, superOverviewLoaded, superOverviewLoading, loadSuperadminOverview]);

  const officeAggregateRows: OfficeAggregateRow[] = useMemo(() => {
    if (!superOverview?.rows?.length) return [];
    const map = new Map<number, OfficeAggregateRow>();
    for (const r of superOverview.rows) {
      const prev = map.get(r.officeId) ?? {
        id: `office-${r.officeId}`,
        officeName: r.officeName,
        routeCount: 0,
        clientCount: 0,
        openLoans: 0,
        closedLoans: 0,
        visitOrderLoansToday: 0,
      };
      prev.routeCount += 1;
      prev.clientCount += Number(r.clientCount ?? 0);
      prev.openLoans += Number(r.openLoans ?? 0);
      prev.closedLoans += Number(r.closedLoans ?? 0);
      prev.visitOrderLoansToday += Number(r.visitOrderLoansToday ?? 0);
      map.set(r.officeId, prev);
    }
    return Array.from(map.values()).sort((a, b) => a.officeName.localeCompare(b.officeName, companyLocale()));
  }, [superOverview]);

  const superDetailRows: RouteDetailRow[] = useMemo(() => {
    if (!superOverview?.rows?.length) return [];
    return superOverview.rows.map((r) => ({
      id: r.routeId,
      officeName: r.officeName,
      routeName: r.routeName,
      clientCount: Number(r.clientCount ?? 0),
      openLoans: Number(r.openLoans ?? 0),
      closedLoans: Number(r.closedLoans ?? 0),
      visitOrderLoansToday: Number(r.visitOrderLoansToday ?? 0),
    }));
  }, [superOverview]);

  const officeAggregateColumns: DataTableColumn<OfficeAggregateRow>[] = useMemo(
    () => [
      { id: 'officeName', header: t('dashboard.col.office'), value: (r) => r.officeName, sortable: true },
      { id: 'routeCount', header: t('dashboard.col.routes'), value: (r) => r.routeCount, render: (r) => count(r.routeCount), align: 'right', sortable: true },
      { id: 'clientCount', header: t('dashboard.col.clients'), value: (r) => r.clientCount, render: (r) => count(r.clientCount), align: 'right', sortable: true },
      { id: 'openLoans', header: t('dashboard.col.openLoans'), value: (r) => r.openLoans, render: (r) => count(r.openLoans), align: 'right', sortable: true },
      { id: 'closedLoans', header: t('dashboard.col.closedLoans'), value: (r) => r.closedLoans, render: (r) => count(r.closedLoans), align: 'right', sortable: true, hideBelow: 'sm' },
      { id: 'visitOrderLoansToday', header: t('dashboard.col.inVisitOrder'), value: (r) => r.visitOrderLoansToday, render: (r) => count(r.visitOrderLoansToday), align: 'right', sortable: true },
    ],
    [t],
  );

  const superDetailColumns: DataTableColumn<RouteDetailRow>[] = useMemo(
    () => [
      { id: 'routeName', header: t('dashboard.col.route'), value: (r) => r.routeName, sortable: true },
      { id: 'officeName', header: t('dashboard.col.office'), value: (r) => r.officeName, sortable: true },
      { id: 'clientCount', header: t('dashboard.col.clients'), value: (r) => r.clientCount, render: (r) => count(r.clientCount), align: 'right', sortable: true },
      { id: 'openLoans', header: t('dashboard.col.openLoans'), value: (r) => r.openLoans, render: (r) => count(r.openLoans), align: 'right', sortable: true },
      { id: 'closedLoans', header: t('dashboard.col.closedLoans'), value: (r) => r.closedLoans, render: (r) => count(r.closedLoans), align: 'right', sortable: true, hideBelow: 'sm' },
      { id: 'visitOrderLoansToday', header: t('dashboard.col.inVisitOrder'), value: (r) => r.visitOrderLoansToday, render: (r) => count(r.visitOrderLoansToday), align: 'right', sortable: true },
    ],
    [t],
  );

  const chartColors = {
    payments: theme.palette.primary.main,
    loans: theme.palette.success.main,
    expenses: theme.palette.error.main,
  };
  const axisTick = { fill: theme.palette.text.secondary, fontSize: 12 };

  const globalStats: Metric[] = superOverview
    ? [
        { key: 'offices', label: t('dashboard.global.offices'), value: count(superOverview.officeCount), hint: t('dashboard.global.officesHint'), icon: 'office' },
        {
          key: 'routes',
          label: t('dashboard.global.routes'),
          value: count(superOverview.routeCount),
          hint: t(isSuperAdmin ? 'dashboard.global.routesHintAll' : 'dashboard.global.routesHintMine'),
          icon: 'route',
        },
        {
          key: 'clients',
          label: t('dashboard.global.clients'),
          value: count(superOverview.totalClients),
          hint: t(isSuperAdmin ? 'dashboard.global.clientsHintAll' : 'dashboard.global.clientsHintMine'),
          icon: 'clients',
        },
        { key: 'open', label: t('dashboard.global.openLoans'), value: count(superOverview.openLoansTotal), hint: t('dashboard.global.openLoansHint'), icon: 'loan' },
        { key: 'closed', label: t('dashboard.global.closedLoans'), value: count(superOverview.closedLoansTotal), hint: t('dashboard.global.closedLoansHint'), icon: 'paid' },
        {
          key: 'visitOrder',
          label: t('dashboard.global.visitOrderLoans'),
          value: count(superOverview.visitOrderLoansTodayTotal),
          hint: superOverview.operationalDate
            ? t('dashboard.global.visitOrderHint', { date: superOverview.operationalDate })
            : t('dashboard.global.visitOrderHintToday'),
          icon: 'reorder',
        },
      ]
    : [];

  // Cada indicador lleva su marca para el recorrido de la pantalla (`<prefijo>-<clave>`).
  const statCards = (items: Metric[], tourPrefix: string) => (
    <Box sx={statGridSx}>
      {items.map((m) => (
        <Box key={m.key} data-tour={`${tourPrefix}-${m.key}`} sx={{ minWidth: 0, display: 'grid' }}>
          <StatCard label={m.label} value={m.value} hint={m.hint} icon={<RuttiaIcon name={m.icon} />} />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ minWidth: 0, width: '100%', maxWidth: 1920, mx: 'auto' }}>
      <PageHeader title={t('dashboard.title')} description={t('dashboard.description')} helpAction={<ScreenTourButton />} />
      <OnboardingCard />

      <Tabs
        value={mainTab}
        onChange={(_, v: number) => setMainTab(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        data-tour="home-tabs"
      >
        <Tab label={t('dashboard.tabDaily')} />
        <Tab label={t('dashboard.tabGlobal')} />
      </Tabs>

      {mainTab === 0 ? (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              component="form"
              onSubmit={(event: React.FormEvent) => {
                event.preventDefault();
                void handleFetchSummary();
              }}
              sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', alignItems: 'center' }}
            >
              <TextField select fullWidth size="small" label={t('common.office')} value={office_id} onChange={(e) => handleOfficeSelect(e.target.value)} data-tour="home-office">
                {offices.map((office) => (
                  <MenuItem key={office.id} value={String(office.id)}>
                    {office.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label={t('common.route')}
                data-tour="home-route"
                value={collect_id}
                disabled={!collectsOffice.length}
                onChange={(e) => handleCollectSelect(e.target.value)}
              >
                {collectsOffice.map((collect) => (
                  <MenuItem key={collect.id} value={String(collect.id)}>
                    {collect.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label={t('dashboard.from')}
                data-tour="home-dates"
                type="date"
                size="small"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: minYmd(endDate, nowOperationalDateString()) }}
              />
              <TextField
                fullWidth
                label={t('dashboard.to')}
                type="date"
                size="small"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate, max: nowOperationalDateString() }}
              />
              <Box>
                <LoadingButton type="submit" variant="contained" loading={summaryLoading} sx={{ minHeight: 40 }} data-tour="home-query">
                  {t('dashboard.query')}
                </LoadingButton>
              </Box>
            </Box>
          </Paper>

          {summaryLoading && !summaryTotals && (
            <Box sx={statGridSx}>
              {[0, 1, 2, 3].map((i) => (
                <CardSkeleton key={i} lines={2} />
              ))}
            </Box>
          )}

          {summaryTotals
            ? statCards(metrics, 'home-stat')
            : !summaryLoading && (
                <Paper variant="outlined" data-tour="home-empty">
                  <EmptyState title={t('dashboard.emptyTitle')} description={t('dashboard.emptyText')} icon={<RuttiaIcon name="summary" fontSize="inherit" />} />
                </Paper>
              )}

          {chartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }} data-tour="home-chart">
              <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                {t('dashboard.chartTitle')}
              </Typography>
              <Box sx={{ width: '100%', height: { xs: 260, md: 320 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={axisTick} stroke={theme.palette.divider} tickFormatter={(value) => moment(value).format('DD/MM')} />
                    <YAxis tick={axisTick} stroke={theme.palette.divider} width={48} tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                    <RechartsTooltip
                      formatter={(value: number) => formatCurrency(Number(value))}
                      labelFormatter={(label) => moment(label).format('DD MMM YYYY')}
                      contentStyle={{
                        background: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        color: theme.palette.text.primary,
                      }}
                    />
                    <Legend wrapperStyle={{ color: theme.palette.text.secondary, fontSize: 13 }} />
                    <Line type="monotone" dataKey="totalPayments" name={t('dashboard.col.payments')} stroke={chartColors.payments} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="totalLoans" name={t('dashboard.col.loans')} stroke={chartColors.loans} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="totalExpenses" name={t('dashboard.col.expenses')} stroke={chartColors.expenses} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.chartEntries', { count: totalEntries })}
              </Typography>
            </Paper>
          )}

          {routeSummaryRows.length > 0 && (
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
              <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                {t('dashboard.byRoute')}
              </Typography>
              <DataTable
                rows={routeSummaryRows}
                columns={routeSummaryColumns}
                getRowId={(r) => r.id}
                exportFileName="resumen-por-ruta"
                tourId="home-by-route"
                ariaLabel={t('dashboard.byRoute')}
                maxHeight="none"
              />
            </Paper>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <Notice severity="info">
            {t(isSuperAdmin ? 'dashboard.global.introAll' : 'dashboard.global.introMine')}{' '}
            {t('dashboard.global.visitOrderNote', { date: superOverview?.operationalDate ? ` (${superOverview.operationalDate})` : '' })}
          </Notice>
          {superOverviewLoading && (
            <Box sx={statGridSx}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <CardSkeleton key={i} lines={2} />
              ))}
            </Box>
          )}
          {superOverviewLoaded && !superOverviewLoading && !superOverview && (
            <Paper variant="outlined">
              <EmptyState title={t('dashboard.global.empty')} icon={<RuttiaIcon name="report" fontSize="inherit" />} />
            </Paper>
          )}
          {superOverview && !superOverviewLoading && (
            <>
              {statCards(globalStats, 'home-global')}
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
                <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                  {t('dashboard.global.byOffice')}
                </Typography>
                <DataTable
                  rows={officeAggregateRows}
                  columns={officeAggregateColumns}
                  getRowId={(r) => r.id}
                  exportFileName="vista-global-por-sede"
                  ariaLabel={t('dashboard.global.byOffice')}
                />
              </Paper>
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
                <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                  {t('dashboard.global.byRoute')}
                </Typography>
                <DataTable
                  rows={superDetailRows}
                  columns={superDetailColumns}
                  getRowId={(r) => r.id}
                  searchable
                  exportFileName="vista-global-por-ruta"
                  ariaLabel={t('dashboard.global.byRoute')}
                />
              </Paper>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
